<template>
  <div>
    <div class="filter-container">
      <el-select
        v-model="query.campagne_agricole_id"
        clearable
        class="mr-15 filter-item"
        style="width: 220px;"
        :placeholder="$t('superviseur.aValider.toutesCampagnes')"
        @change="handleFilter"
      >
        <el-option
          v-for="campagne in campagnes"
          :key="campagne.id"
          :label="campagne.nom"
          :value="campagne.id"
        />
      </el-select>

      <el-select
        v-model="query.statut"
        clearable
        class="mr-15 filter-item"
        style="width: 180px;"
        :placeholder="$t('controleInterne.supervision.statut')"
        @change="handleFilter"
      >
        <el-option :label="$t('controleInterne.supervision.enAttente')" value="en_attente" />
        <el-option :label="$t('controleInterne.supervision.approuve')" value="approuve" />
        <el-option :label="$t('controleInterne.supervision.rejete')" value="rejete" />
        <el-option :label="$t('controleInterne.supervision.aCorriger')" value="a_corriger" />
      </el-select>

      <el-input
        v-model="query.keyword"
        :placeholder="$t('general.keyword')"
        class="wd-200 mr-15 filter-item"
        @keyup.enter.native="handleFilter"
      />
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
        {{ $t('general.search') }}
      </el-button>
    </div>

    <el-table v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%">
      <el-table-column align="center" :label="$t('general.code')" min-width="120">
        <template slot-scope="scope">
          <span>{{ scope.row.code }}</span>
        </template>
      </el-table-column>

      <el-table-column v-if="type === 'formation'" align="center" :label="$t('formation.theme')" min-width="160">
        <template slot-scope="scope">
          <span>{{ scope.row.theme }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('controleur.controleur')" min-width="160">
        <template slot-scope="scope">
          <span>{{ scope.row.controleur }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('controleInterne.supervision.statut')" width="130">
        <template slot-scope="scope">
          <el-tag :type="statutTagType(scope.row.statut_supervision)" size="mini">
            {{ statutLabel(scope.row.statut_supervision) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column align="center" :label="$t('general.actions')" width="120">
        <template slot-scope="scope">
          <el-button type="primary" size="small" @click="openReview(scope.row)">
            {{ $t('controleInterne.supervision.review') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      v-if="total > 0"
      :total="total"
      :page.sync="query.page"
      :limit.sync="query.limit"
      @pagination="getList"
    />

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="60%"
    >
      <div v-if="selectedRow">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item :label="$t('general.code')">{{ selectedRow.code }}</el-descriptions-item>
          <el-descriptions-item :label="$t('controleur.controleur')">{{ selectedRow.controleur }}</el-descriptions-item>
          <el-descriptions-item v-if="type === 'formation'" :label="$t('formation.theme')">{{ selectedRow.theme }}</el-descriptions-item>
          <el-descriptions-item v-if="type === 'formation'" :label="$t('formation.lieu')">{{ selectedRow.lieu }}</el-descriptions-item>
          <el-descriptions-item v-if="type === 'audit'" :label="$t('superviseur.aValider.dateAudit')">{{ selectedRow.date_audit }}</el-descriptions-item>
        </el-descriptions>

        <div v-if="type === 'formation' && selectedRow.resume" style="margin-top: 16px;">
          <h4>{{ $t('formation.resume') }}</h4>
          <p>{{ selectedRow.resume }}</p>
        </div>

        <div v-if="type === 'audit' && selectedRow.commentaire" style="margin-top: 16px;">
          <h4>{{ $t('controleInterne.supervision.commentaire') }}</h4>
          <p>{{ selectedRow.commentaire }}</p>
        </div>

        <div style="margin-top: 16px;">
          <el-button type="text" icon="el-icon-document" :loading="pdfLoading" @click="openPdf">
            {{ $t('superviseur.aValider.voirPdf') }}
          </el-button>
        </div>

        <el-form :model="approbationForm" label-position="top" style="margin-top: 16px;">
          <el-form-item :label="$t('controleInterne.supervision.decision')">
            <el-radio-group v-model="approbationForm.statut">
              <el-radio label="approuve">{{ $t('controleInterne.supervision.approuve') }}</el-radio>
              <el-radio label="a_corriger">{{ $t('controleInterne.supervision.aCorriger') }}</el-radio>
              <el-radio label="rejete">{{ $t('controleInterne.supervision.rejete') }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$t('controleInterne.supervision.commentaire')">
            <el-input v-model="approbationForm.commentaire" type="textarea" :rows="3" />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('general.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitApprobation">
          {{ $t('general.save') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import Resource from '@/api/resource';
import waves from '@/directive/waves';
import {
  getSuperviseurActivites,
  approuverFormationSuperviseur,
  approuverAuditSuperviseur,
} from '@/api/superviseur';

const campagneResource = new Resource('campagne_agricoles');
const formationRessourcePdf = new Resource('fiche/formation');
const auditRessourcePdf = new Resource('controleurs/fiche/audit');

export default {
  name: 'SuperviseurActivitesList',
  components: { Pagination },
  directives: { waves },
  props: {
    type: {
      type: String,
      required: true,
      validator: value => ['formation', 'audit'].includes(value),
    },
  },
  data() {
    return {
      list: [],
      campagnes: [],
      total: 0,
      loading: false,
      saving: false,
      pdfLoading: false,
      dialogVisible: false,
      selectedRow: null,
      query: {
        page: 1,
        limit: 20,
        keyword: '',
        statut: 'en_attente',
        campagne_agricole_id: null,
        type: '',
      },
      approbationForm: {
        statut: 'approuve',
        commentaire: '',
      },
    };
  },
  computed: {
    dialogTitle() {
      if (!this.selectedRow) {
        return '';
      }
      return this.$t('controleInterne.supervision.reviewTitle', { code: this.selectedRow.code });
    },
  },
  watch: {
    type(newType) {
      this.query.type = newType;
      this.query.page = 1;
      this.getList();
    },
  },
  created() {
    this.query.type = this.type;
    this.loadCampagnes();
  },
  methods: {
    statutLabel(statut) {
      const map = {
        en_attente: this.$t('controleInterne.supervision.enAttente'),
        approuve: this.$t('controleInterne.supervision.approuve'),
        rejete: this.$t('controleInterne.supervision.rejete'),
        a_corriger: this.$t('controleInterne.supervision.aCorriger'),
      };
      return map[statut] || statut;
    },
    statutTagType(statut) {
      if (statut === 'approuve') {
        return 'success';
      }
      if (statut === 'rejete') {
        return 'danger';
      }
      if (statut === 'a_corriger') {
        return 'warning';
      }
      return 'info';
    },
    async loadCampagnes() {
      const { data } = await campagneResource.list({ limit: 100 });
      this.campagnes = data || [];
      const routeCampagneId = parseInt(this.$route.query.campagne_agricole_id, 10);
      if (routeCampagneId && this.campagnes.some(c => c.id === routeCampagneId)) {
        this.query.campagne_agricole_id = routeCampagneId;
      } else {
        try {
          const { data: current } = await campagneResource.sublist('/en_cours');
          this.query.campagne_agricole_id = current && current.id ? current.id : (this.campagnes[0] ? this.campagnes[0].id : null);
        } catch (error) {
          this.query.campagne_agricole_id = this.campagnes[0] ? this.campagnes[0].id : null;
        }
      }
      this.getList();
    },
    async getList() {
      this.loading = true;
      try {
        const response = await getSuperviseurActivites(this.query);
        this.list = response.data || [];
        this.total = response.meta ? response.meta.total : this.list.length;
      } finally {
        this.loading = false;
      }
    },
    handleFilter() {
      this.query.page = 1;
      this.getList();
    },
    openReview(row) {
      this.selectedRow = row;
      this.approbationForm = {
        statut: row.approbation_superviseur && row.approbation_superviseur.statut !== 'en_attente'
          ? row.approbation_superviseur.statut
          : 'approuve',
        commentaire: row.approbation_superviseur ? (row.approbation_superviseur.commentaire || '') : '',
      };
      this.dialogVisible = true;
    },
    async openPdf() {
      if (!this.selectedRow) {
        return;
      }
      this.pdfLoading = true;
      try {
        const campId = this.selectedRow.campagne_agricole_id || this.$store.getters.camp_id;
        let pdfUrl = '';
        if (this.type === 'formation') {
          const { data } = await formationRessourcePdf.getPdf(this.selectedRow.id, campId);
          pdfUrl = data;
        } else if (this.type === 'audit') {
          const { data } = await auditRessourcePdf.getAuditPdf(
            this.selectedRow.controleur_id,
            this.selectedRow.id,
            campId
          );
          pdfUrl = data;
        }
        if (pdfUrl) {
          window.open(pdfUrl, '_blank');
        }
      } catch (error) {
        console.error(error);
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : this.$t('general.errorOccurred');
        this.$message.error(msg);
      } finally {
        this.pdfLoading = false;
      }
    },
    async submitApprobation() {
      if (!this.selectedRow) {
        return;
      }
      this.saving = true;
      try {
        if (this.type === 'formation') {
          await approuverFormationSuperviseur(this.selectedRow.id, this.approbationForm);
        } else {
          await approuverAuditSuperviseur(this.selectedRow.id, this.approbationForm);
        }
        this.$message.success(this.$t('controleInterne.supervision.saved'));
        this.dialogVisible = false;
        this.getList();
      } catch (error) {
        console.error(error);
        const msg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : this.$t('general.errorOccurred');
        this.$message.error(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
